import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface QuoteTemplateRow {
  id: string;
  name: string;
  description: string | null;
  service_type: string;
  notes: string | null;
  guest_count: number | null;
  event_type_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items?: QuoteTemplateItemRow[];
}

export interface QuoteTemplateItemRow {
  id: string;
  template_id: string;
  menu_item_id: string | null;
  package_id: string | null;
  name: string;
  description: string | null;
  pricing_type: string;
  unit_price: number;
  quantity: number;
  sort_order: number;
}

export function useQuoteTemplates() {
  return useQuery({
    queryKey: ["quote_templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_templates")
        .select("*, items:quote_template_items(*)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as QuoteTemplateRow[];
    },
  });
}

export interface CreateTemplateInput {
  name: string;
  description?: string;
  service_type: string;
  notes?: string | null;
  guest_count?: number | null;
  event_type_id?: string | null;
  items: Omit<QuoteTemplateItemRow, "id" | "template_id">[];
}

export function useCreateQuoteTemplate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ items, ...values }: CreateTemplateInput) => {
      const { data, error } = await supabase
        .from("quote_templates")
        .insert({ ...values, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;

      if (items.length > 0) {
        const { error: ie } = await supabase
          .from("quote_template_items")
          .insert(items.map((it, i) => ({ ...it, template_id: data.id, sort_order: i })));
        if (ie) {
          // Don't leave an empty template behind. The items error is the one worth surfacing.
          const { error: cleanupError } = await supabase.from("quote_templates").delete().eq("id", data.id);
          if (cleanupError) console.warn("Could not remove partially saved quote template", data.id);
          throw ie;
        }
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote_templates"] });
      toast.success("Template saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export interface UpdateTemplateInput {
  id: string;
  name: string;
  description: string | null;
  notes: string | null;
  guest_count: number | null;
  /** The template's full item list in its new order; replaces the stored items (#847). */
  items: CreateTemplateInput["items"];
}

export function useUpdateQuoteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, items, ...values }: UpdateTemplateInput) => {
      // Replace the items: insert the new set, then delete the old rows. Inserting first
      // means a failed write never leaves the template with no items.
      const [{ error }, { data: oldRows, error: re }] = await Promise.all([
        supabase.from("quote_templates").update(values).eq("id", id),
        supabase.from("quote_template_items").select("id").eq("template_id", id),
      ]);
      if (error) throw error;
      if (re) throw re;

      let newIds: string[] = [];
      if (items.length > 0) {
        const { data: inserted, error: ie } = await supabase
          .from("quote_template_items")
          .insert(items.map((it, i) => ({ ...it, template_id: id, sort_order: i })))
          .select("id");
        if (ie) throw ie;
        newIds = (inserted ?? []).map((r) => r.id);
      }

      const oldIds = (oldRows ?? []).map((r) => r.id);
      if (oldIds.length > 0) {
        const { error: de } = await supabase.from("quote_template_items").delete().in("id", oldIds);
        if (de) {
          // Take the new rows back out so the template isn't left with both sets.
          if (newIds.length > 0) {
            const { error: cleanupError } = await supabase.from("quote_template_items").delete().in("id", newIds);
            if (cleanupError) console.warn("Could not remove partially saved quote template items", id);
          }
          throw de;
        }
      }
    },
    // Returned so mutateAsync waits for the refetch: "Use template" right after Save
    // must see the new items.
    onSuccess: () => {
      toast.success("Template updated");
      return qc.invalidateQueries({ queryKey: ["quote_templates"] });
    },
    onError: (e: Error) => {
      // The name or notes may have saved before the items failed.
      qc.invalidateQueries({ queryKey: ["quote_templates"] });
      toast.error(e.message);
    },
  });
}

export function useDeleteQuoteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("quote_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote_templates"] });
      toast.success("Template deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
